import { useTranslation } from "react-i18next";
import { useEventInstance, useFormStore } from "@/widgets/classic-form/form";
import { BirthYearCalculator } from "@/features/birth-year-calculator";
import { FieldComposer, FieldComposerFieldType } from "@/features/form-fields";
import styles from "./BirthCalculator.module.scss";

const BIRTH_FIELD_PATH = "events.SelfBirth.instances.0.date.year" as const;

export interface BirthCalculatorProps {
    /** Persisted calculator age value from parent */
    calculatorAge?: string;
    /** Persisted calculator year value from parent */
    calculatorYear?: string;
    /** Callback to persist calculator values in parent state */
    onCalculatorValuesChange?: (age: string, year: string) => void;
}

export const BirthCalculator = ({ calculatorAge, calculatorYear, onCalculatorValuesChange }: BirthCalculatorProps) => {
    const { t } = useTranslation();
    const { setYear } = useEventInstance({ eventName: "SelfBirth" });
    const givenName = useFormStore(({ values }) => values.name.givenName);

    const onCalculatorSubmit = (year: number) => {
        setYear(year);
    };

    return (
        <div className={styles.birthYearStep}>
            <FieldComposer
                fieldPath={BIRTH_FIELD_PATH}
                fieldConfig={{
                    fieldType: FieldComposerFieldType.Place,
                    label: (
                        <p>
                            <strong>{t("searchAssist.birthYear")}</strong>
                            <span className={styles.optional}>{t("searchAssist.optional")}</span>
                            <div>{t("searchAssist.guessWithin10Years")}</div>
                        </p>
                    ),
                    required: false,
                }}
            >
                <FieldComposer.Label />
                <div className={styles.inputRow}>
                    <span className="icon iconCalendar" />
                    <div className="calculatorInput w20">
                        <FieldComposer.FieldConnector>
                            {({ formField }, id) => (
                                <FieldComposer.Inputs.Year
                                    id={id}
                                    {...formField}
                                    placeholder={t("searchAssist.birthYearPlaceholder")}
                                />
                            )}
                        </FieldComposer.FieldConnector>
                    </div>
                    <div className={`callout open willTransform calloutPositionBottom ${styles.assistCalculator}`}>
                        <div className="calloutPointer willTransform">
                            <div className="calloutPointerShadow" />
                        </div>
                        <div className="calloutContent" tabIndex={-1}>
                            <BirthYearCalculator
                                onSubmit={onCalculatorSubmit}
                                inline
                                aboutText={`${givenName} ${t("searchAssist.wasAbout")}`}
                                initialAge={calculatorAge ?? ""}
                                initialYear={calculatorYear ?? ""}
                                onValuesChange={onCalculatorValuesChange}
                            />
                        </div>
                    </div>
                </div>
            </FieldComposer>
        </div>
    );
};
